import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { EntityDefinition, FieldConfig } from '../../types/entity-definition.interface';
import { EntityFormValue } from '../../types/entity-form-result.interface';
import { Location } from '../../types/location.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

type LocationDialogConfig = DynamicDialogConfig<EntityDialogData<Location>>;
type LocationDialogRef = DynamicDialogRef<LocationFormComponent>;

@Component({
  selector: 'app-location-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, GenericEntityFormComponent],
  templateUrl: './location-form.component.html',
})
export class LocationFormComponent implements OnInit {
  @Input() entityDefinition?: EntityDefinition<Location>;
  @Input() entity?: Location;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<Location>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<Location>();

  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslocoService);

  private readonly config = inject<LocationDialogConfig | null>(DynamicDialogConfig, { optional: true });
  private readonly dialogRef = inject<LocationDialogRef | null>(DynamicDialogRef, { optional: true });

  ngOnInit(): void {
    this.initializeFormState();
  }

  private initializeFormState(): void {
    const assetId = this.determineAssetId();

    if (!assetId || assetId <= 0) {
      console.error(this.translate.translate('common.errors.invalidAssetIdForLocation'));
      return;
    }

    this.setupEntityData(assetId);

    this.validateEntityConfig();
  }

  private determineAssetId(): number | null {
    const entityAssetId = this.getEntityAssetId();
    if (entityAssetId) return entityAssetId;

    const configAssetId = this.getConfigAssetId();
    if (configAssetId) return configAssetId;

    return this.getRouteAssetId();
  }

  private getEntityAssetId(): number | null {
    if (this.entity?.assetId && this.entity.assetId > 0) {
      return this.entity.assetId;
    }
    return null;
  }

  private getConfigAssetId(): number | null {
    const dialogAssetId = this.coerceAssetId(this.config?.data?.additionalData?.['assetId']);
    if (dialogAssetId) return dialogAssetId;

    return this.coerceAssetId(this.additionalData?.['assetId']);
  }

  private coerceAssetId(raw: unknown): number | null {
    if (raw === undefined || raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private getRouteAssetId(): number | null {
    const routeParams = this.route.snapshot.params;
    if (routeParams['id']) {
      const assetId = Number(routeParams['id']);
      if (assetId > 0) return assetId;
    }

    return this.getParentRouteAssetId();
  }

  private getParentRouteAssetId(): number | null {
    let currentRoute = this.route.parent;
    while (currentRoute) {
      if (currentRoute.snapshot.params['id']) {
        const assetId = Number(currentRoute.snapshot.params['id']);
        if (assetId > 0) return assetId;
      }
      currentRoute = currentRoute.parent;
    }

    return null;
  }

  private setupEntityData(assetId: number): void {
    if (this.mode === 'create' && !this.entity?.assetId) {
      const previous = this.entity;
      this.entity = {
        id: previous?.id ?? 0,
        name: previous?.name ?? '',
        isUsed: previous?.isUsed ?? false,
        ...(previous ?? {}),
        assetId,
      };
    }

    this.additionalData = {
      ...(this.additionalData ?? {}),
      assetId,
    };
  }

  private validateEntityConfig(): void {
    if (!this.entityDefinition?.fields) return;

    const assetIdField = this.entityDefinition.fields.find((field: FieldConfig) => field.field === 'assetId');

    if (!assetIdField) {
      console.warn(this.translate.translate('common.errors.assetIdNotDefined'));
    } else if (!assetIdField.required) {
      console.warn(this.translate.translate('common.errors.assetIdNotRequired'));
    }
  }

  onFormSubmit(formData: EntityFormValue<Location>): void {
    const assetId = this.determineAssetId();

    if (!assetId || assetId <= 0) {
      console.error(this.translate.translate('common.errors.submitWithoutValidAssetId'));
      return;
    }

    const payload: EntityFormValue<Location> = { ...formData, assetId };

    if (this.dialogRef) {
      this.dialogRef.close(payload);
    } else {
      this.formSubmit.emit(payload);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDeleteRequest(entity: Location): void {
    const onDelete = this.config?.data?.onDelete;

    if (onDelete) {
      onDelete(entity);
      this.dialogRef?.close();
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close({ __action: 'delete', entity });
    } else {
      this.deleteEntity.emit(entity);
    }
  }
}
